import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Table,
} from "react-bootstrap";
import {
  FaRocket,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileAlt,
  FaDownload,
  FaMagic,
} from "react-icons/fa";
import Layout from "./Layout";
import axios from "axios";
import "../assets/css/SpAutoMultiTarget.css";

const SpAutoMultiTarget = () => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

  // Targeting Groups Configuration
  const TARGETING_GROUPS = [
    {
      id: "CLOSE_MATCH",
      name: "Close Match",
      description: "Close searches",
      icon: "Target",
      color: "#28a745",
    },
    {
      id: "LOOSE_MATCH",
      name: "Loose Match",
      description: "Related searches",
      icon: "Link",
      color: "#17a2b8",
    },
    {
      id: "SUBSTITUTES",
      name: "Substitutes",
      description: "Substitute products",
      icon: "Swap",
      color: "#ffc107",
    },
    {
      id: "COMPLEMENTS",
      name: "Complements",
      description: "Complementary products",
      icon: "+",
      color: "#6f42c1",
    },
  ];

  // States
  const [siteType, setSiteType] = useState("AMAZON_AND_BEYOND");
  const [productsFile, setProductsFile] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([
    "CLOSE_MATCH",
    "LOOSE_MATCH",
  ]); // Default: 2 selected

  // Campaign config
  const [defaultBid, setDefaultBid] = useState(0.75);
  const [defaultBudget, setDefaultBudget] = useState(10);
  const [campaignState, setCampaignState] = useState("PAUSED");
  const [biddingStrategy, setBiddingStrategy] = useState("SALES_DOWN_ONLY");

  // UI states
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [creationComplete, setCreationComplete] = useState(false);

  // Handle file upload
  const handleProductsFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setProductsFile(file);
      setError(null);
    } else {
      setError("Please select a valid CSV file");
    }
  };

  // Toggle targeting group selection
  const toggleTargetingGroup = (groupId) => {
    if (selectedGroups.includes(groupId)) {
      // Don't allow deselecting if only one left
      if (selectedGroups.length > 1) {
        setSelectedGroups(selectedGroups.filter((g) => g !== groupId));
      }
    } else {
      setSelectedGroups([...selectedGroups, groupId]);
    }
  };

  // Select all groups
  const selectAllGroups = () => {
    setSelectedGroups(TARGETING_GROUPS.map((g) => g.id));
  };

  // Generate campaigns
  const handleGenerateCampaigns = async () => {
    if (!productsFile) {
      setError("You must upload the products file");
      return;
    }

    if (selectedGroups.length < 2) {
      setError("You must select at least 2 targeting groups");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("products_file", productsFile);
      formData.append("site_type", siteType);
      formData.append("default_bid", defaultBid);
      formData.append("default_budget", defaultBudget);
      formData.append("bidding_strategy", biddingStrategy);
      formData.append("targeting_groups", JSON.stringify(selectedGroups));

      const response = await axios.post(
        `${API_URL}/sp-auto-multi/generate`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (response.data.success) {
        const generatedCampaigns = response.data.campaigns || [];
        setCampaigns(generatedCampaigns.map((c) => ({ ...c, selected: true })));
        setResult({
          success: true,
          message: `${generatedCampaigns.length} automatic campaigns generated with ${selectedGroups.length} targeting groups.`,
          count: generatedCampaigns.length,
        });
      } else {
        setError(response.data.error || "Error generating campaigns");
      }
    } catch (err) {
      setError(err.response.data.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Create in Amazon
  const handleCreateInAmazon = async () => {
    const selected = campaigns.filter((c) => c.selected);
    if (selected.length === 0) {
      setError("Select at least one campaign");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const profileId = localStorage.getItem("selectedAccountValue");
      if (!profileId) {
        setError("Select an account first");
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/sp-auto-multi/create-in-amazon`,
        {
          profileId,
          campaigns: selected,
          campaignState,
          siteType,
          biddingStrategy,
          targetingGroups: selectedGroups,
        },
      );

      if (response.data.success) {
        setResult({
          success: true,
          message: response.data.message,
          count: response.data.campaigns_created,
        });
        setCampaigns(
          response.data.results.map((r) => ({
            "Campaign Name": r.campaignName,
            "Campaign ID": r.campaignId,
            "Active Groups":
              r.activeGroups.join(", ") || selectedGroups.join(", "),
            Status: "Created",
          })) || [],
        );
        setCreationComplete(true);
      } else {
        setError(response.data.error);
      }
    } catch (err) {
      setError(err.response.data.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Selection handlers
  const toggleCampaignSelection = (idx) => {
    const updated = [...campaigns];
    updated[idx].selected = !updated[idx].selected;
    setCampaigns(updated);
  };

  const selectAllCampaigns = () =>
    setCampaigns(campaigns.map((c) => ({ ...c, selected: true })));
  const deselectAllCampaigns = () =>
    setCampaigns(campaigns.map((c) => ({ ...c, selected: false })));

  // Download
  const handleDownloadCampaigns = () => {
    if (!campaigns.length) return;
    const headers = Object.keys(campaigns[0]).filter((h) => h !== "selected");
    const csv = [
      headers.join(","),
      ...campaigns.map((c) => headers.map((h) => c[h]).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `sp_auto_multi_${Date.now()}.csv`;
    link.click();
  };

  // Reset
  const handleReset = () => {
    setProductsFile(null);
    setCampaigns([]);
    setResult(null);
    setError(null);
    setCreationComplete(false);
  };

  return (
    <Layout>
      <Container fluid className="sp-auto-multi-page py-4">
        <Row className="mb-4">
          <Col>
            <h2 className="text-white mb-3">
              <FaMagic className="me-2" />
              Auto Multi Target - Sponsored Products
            </h2>
            <p className="text-muted">
              Automatic campaigns with MULTIPLE active targeting groups (2 or
              more)
            </p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <FaExclamationTriangle className="me-2" />
            {error}
          </Alert>
        )}

        {result.success && !creationComplete && (
          <Alert variant="success" onClose={() => setResult(null)} dismissible>
            <FaCheckCircle className="me-2" />
            {result.message}
          </Alert>
        )}

        {/* Configuration */}
        {!result.amazonResults && !creationComplete && (
          <>
            <Alert variant="warning" className="mb-4">
              <FaMagic className="me-2" />
              <strong>Automatic Targeting:</strong> Select 2 or more targeting
              groups to activate
            </Alert>

            {/* SITE */}
            <div className="config-section-card mb-4">
              <h5>
                <Badge bg="primary" className="me-2">
                  1
                </Badge>
                Select SITE
              </h5>
              <Form.Select
                value={siteType}
                onChange={(e) => setSiteType(e.target.value)}
                className="site-selector mt-3"
              >
                <option value="AMAZON_AND_BEYOND">Amazon and Beyond</option>
                <option value="AMAZON_BUSINESS">Amazon Business</option>
              </Form.Select>
            </div>

            {/* Upload */}
            <div className="upload-grid">
              <div className={`upload-card-new ${productsFile ? "has-file" : ""}`}
              >
                <div className="card-required-badge">Required</div>
                <Badge bg="primary" className="step-badge">
                  2
                </Badge>
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">Products CSV</div>
                <div className="upload-card-desc">ASIN, SKU</div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {productsFile ? "Change" : "Select"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleProductsFileChange}
                    hidden
                  />
                </label>
                {productsFile && (
                  <small className="text-success mt-2">
                    {productsFile.name}
                  </small>
                )}
              </div>
            </div>

            {/* Multi-Select Targeting Groups */}
            <div className="config-section-card mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  <Badge bg="primary" className="me-2">
                    3
                  </Badge>
                  Select Targeting Groups (2 or more)
                </h5>
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={selectAllGroups}
                >
                  Select All
                </Button>
              </div>
              <p className="text-muted small mb-3">
                Selected:{" "}
                <strong className="text-warning">
                  {selectedGroups.length}
                </strong>{" "}
                de 4
              </p>

              <div className="targeting-groups-grid">
                {TARGETING_GROUPS.map((group) => (
                  <div
                    key={group.id}
                    className={`targeting-group-card ${selectedGroups.includes(group.id) ? "selected" : ""}`}
                    onClick={() => toggleTargetingGroup(group.id)}
                    style={{ "--group-color": group.color }}
                  >
                    <div className="group-checkbox">
                      <Form.Check
                        type="checkbox"
                        checked={selectedGroups.includes(group.id)}
                        onChange={() => {}}
                        className="group-check"
                      />
                    </div>
                    <div className="group-icon">{group.icon}</div>
                    <div className="group-name">{group.name}</div>
                    <div className="group-description">{group.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Config */}
            <div className="config-section-card mt-4">
              <h5>
                <Badge bg="primary" className="me-2">
                  4
                </Badge>
                Configuration
              </h5>
              <Row className="mt-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="text-muted">Bid ($)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={defaultBid}
                      onChange={(e) =>
                        setDefaultBid(parseFloat(e.target.value))
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="text-muted">Budget ($)</Form.Label>
                    <Form.Control
                      type="number"
                      value={defaultBudget}
                      onChange={(e) =>
                        setDefaultBudget(parseFloat(e.target.value))
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="text-muted">
                      Bidding Strategy
                    </Form.Label>
                    <Form.Select
                      value={biddingStrategy}
                      onChange={(e) => setBiddingStrategy(e.target.value)}
                    >
                      <option value="SALES_DOWN_ONLY">Down Only</option>
                      <option value="SALES_UP_AND_DOWN">Up and Down</option>
                      <option value="FIXED_BID">Fixed</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="text-muted">Targeting</Form.Label>
                    <Form.Control
                      type="text"
                      value="AUTOMATIC"
                      disabled
                      className="bg-dark text-warning fw-bold"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Generate */}
            <div className="generate-section mt-4">
              <button className={`btn-generate-campaigns ${!loading && productsFile && selectedGroups.length >= 2 ? "ready" : ""}`}
                onClick={handleGenerateCampaigns}
                disabled={loading || !productsFile || selectedGroups.length < 2}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FaMagic /> Generate Multi Campaigns ({selectedGroups.length}{" "}
                    groups)
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* Results */}
        {campaigns.length > 0 && (
          <Row className="mt-4">
            <Col>
              <Card className="config-section-card border-0 p-0 overflow-hidden">
                <Card.Header className="bg-dark text-white p-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5>
                      <FaMagic className="me-2" />
                      Auto Multi Campaigns ({campaigns.length})
                    </h5>
                    <div className="d-flex gap-2">
                      <Form.Select
                        size="sm"
                        value={campaignState}
                        onChange={(e) => setCampaignState(e.target.value)}
                        disabled={creationComplete}
                      >
                        <option value="ENABLED">Enabled</option>
                        <option value="PAUSED">Paused</option>
                      </Form.Select>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleDownloadCampaigns}
                      >
                        <FaDownload />
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleReset}
                      >
                        <FaRocket />
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body
                  className="p-0 table-responsive bg-dark" style={{ maxHeight : "400px" }}
                >
                  <Table hover variant="dark" size="sm" className="mb-0">
                    <thead className="text-warning sticky-top bg-dark">
                      <tr>
                        <th>
                          <Form.Check
                            checked={campaigns.every((c) => c.selected)}
                            onChange={(e) =>
                              e.target.checked ? selectAllCampaigns() : deselectAllCampaigns()
                            }
                          />
                        </th>
                        <th>#</th>
                        <th>Campaign</th>
                        <th>ASIN</th>
                        <th>Active Groups</th>
                        <th>Bid</th>
                        <th>Budget</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((c, i) => (
                        <tr key={i}>
                          <td>
                            <Form.Check
                              checked={c.selected || false}
                              onChange={() => toggleCampaignSelection(i)}
                            />
                          </td>
                          <td>{i + 1}</td>
                          <td
                            className="text-truncate" style={{ maxWidth : "200px" }}
                          >
                            {c["Campaign Name"]}
                          </td>
                          <td className="text-info">{c.ASIN}</td>
                          <td>
                            <Badge bg="warning" text="dark">
                              {c["Active Groups"] || selectedGroups.length}
                            </Badge>
                          </td>
                          <td>${c.Bid}</td>
                          <td>${c.Budget}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
                <Card.Footer className="bg-dark d-flex justify-content-between p-3">
                  <Button variant="secondary" onClick={handleReset}>
                    Back
                  </Button>
                  <Button
                    variant="warning"
                    onClick={handleCreateInAmazon}
                    disabled={
                      loading ||
                      creationComplete ||
                      !campaigns.filter((c) => c.selected).length
                    }
                  >
                    {loading ? (
                      <Spinner size="sm" />
                    ) : creationComplete ? (
                      <>
                          <FaCheckCircle /> Created
                      </>
                    ) : (
                      "Create in Amazon"
                    )}
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        )}

        {/* Requirements */}
        {!campaigns.length && (
          <div className="requirements-section mt-4">
            <span className="requirements-title">Auto Multi Target</span>
            <div className="req-item">
              <strong>Targeting:</strong> AUTOMATIC (fixed)
            </div>
            <div className="req-item">
              <strong>Groups:</strong> Select 2 or more to activate
              simultaneously
            </div>
            <div className="req-item">
              <strong>Options:</strong> Close Match, Loose Match, Substitutes,
              Complements
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default SpAutoMultiTarget;

